import React, { useState, useCallback } from 'react'

import styles from './ExploitButton.module.css'

interface ExploitButtonProps {
  children: React.ReactNode
  onClick?: () => void
  variant?: 'danger' | 'info' | 'warning'
  disabled?: boolean
  className?: string
}

interface Particle {
  id: number
  x: number
  y: number
  vx: number
  vy: number
}

const ExploitButton: React.FC<ExploitButtonProps> = ({
  children,
  onClick,
  variant = 'danger',
  disabled = false,
  className = '',
}) => {
  const [particles, setParticles] = useState<Particle[]>([])
  const [isShaking, setIsShaking] = useState(false)

  const createParticles = useCallback((e: React.MouseEvent<HTMLButtonElement>) => {
    const rect = e.currentTarget.getBoundingClientRect()
    const x = e.clientX - rect.left
    const y = e.clientY - rect.top

    const newParticles: Particle[] = []
    for (let i = 0; i < 12; i++) {
      const angle = (Math.PI * 2 * i) / 12
      const velocity = 3 + Math.random() * 3
      newParticles.push({
        id: Date.now() + i,
        x,
        y,
        vx: Math.cos(angle) * velocity,
        vy: Math.sin(angle) * velocity,
      })
    }
    setParticles(newParticles)

    setTimeout(() => setParticles([]), 600)
  }, [])

  const handleClick = (e: React.MouseEvent<HTMLButtonElement>) => {
    if (disabled) return

    createParticles(e)
    setIsShaking(true)
    setTimeout(() => setIsShaking(false), 200)

    onClick?.()
  }

  return (
    <button
      className={`${styles.exploitButton} ${styles[variant]} ${isShaking ? styles.shake : ''} ${className}`}
      onClick={handleClick}
      disabled={disabled}
    >
      <span className={styles.buttonText}>{children}</span>
      <span className={styles.scanline} />
      {particles.map((particle) => (
        <span
          key={particle.id}
          className={styles.particle}
          style={{
            '--start-x': `${particle.x}px`,
            '--start-y': `${particle.y}px`,
            '--end-x': `${particle.x + particle.vx * 30}px`,
            '--end-y': `${particle.y + particle.vy * 30}px`,
          } as React.CSSProperties}
        />
      ))}
    </button>
  )
}

export default ExploitButton
